import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { Invoice, UpcomingInvoice } from "../types/Invoice";
import { doNotRetryOnUnrecoverableError } from "../utils/apiUtil";
import { toInvoice, toUpcomingInvoice } from "../utils/mappers/invoice";
import { minutes } from "../utils/timeUtil";
import { AxiosInstance, AxiosResponse } from "axios";
import CustomerId from "../state/CustomerId";
import { useClient } from "../state/client";
import { initialized } from "../utils/initializedUtil";
import { MoreAppError } from "../MoreAppError";

export type CouponDto = {
  id: string;
  name: string;
  amountOff?: number;
  currency?: string;
  duration: string;
  durationInMonths?: number;
  percentOff?: number;
};

export type DiscountDto = {
  coupon: CouponDto;
  start: number;
  end?: number;
  couponCode?: string;
};

export type AddCouponRequest = {
  coupon: string;
};

export type CancelSubscriptionRequest = {
  subscription: string;
  terminationReasons: string[];
  message: string;
};

export type PaymentStatusDto = {
  total: number;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
};

export type InvoiceDto = {
  invoiceNumber: string;
  invoiceDate: string;
  created: number;
  pdfUrl: string;
  statusUrl: string;
  invoiceStatus: string;
  paymentStatus: PaymentStatusDto;
};

export type InvoiceLineDto = {
  description: string;
  amount: number;
  name: string;
  type: string;
  interval: string;
};

export type UpcomingInvoiceDto = {
  invoiceLines: InvoiceLineDto[];
  discount?: DiscountDto;
  tax: number;
  taxPercent?: number;
  subTotal: number;
  total: number;
  amountDue: number;
  startingBalance?: number;
  endingBalance?: number;
  currency: string;
  invoiceDate: number;
  scheduleChange: boolean;
};

export type SubscriptionStatusDto = {
  activeSubscription: boolean;
  blockedSubscription: boolean;
  unblockedSubscription: boolean;
  disputedSubscription: boolean;
  unpaidInvoices: boolean;
  pastDueInvoices: boolean;
  trialExceeded: boolean;
  trialing: boolean;
  defaultPaymentMethodExpired: boolean;
};

export type BillingProfileDto = {
  reference: number;
  billingAddress: BillingAddressDto;
  vatNumber?: string;
  companyIdentifier?: string;
  contactEmailAddress: string;
  billingEmailAddress: string;
  phoneNumber: string;
  companyName: string;
  invoiceFields: InvoiceFieldDto[];
  stripeAccount: string;
  stripeCustomerId: string;
};

export type ScheduleChangeDto = {
  id: string;
  product: string;
  name: string;
  interval: string;
  type: string;
};

export type SubscriptionScheduleDto = {
  on: number;
  current: ScheduleChangeDto[];
  future: ScheduleChangeDto[];
  isPricingChange: boolean;
};

export type SubscriptionItemDto = {
  id: string;
  product: string;
  type: string;
  tiers: UsageTierDto[];
};

export type SubscriptionPlanDto = {
  id: string;
  lookupKey?: string;
  subscription?: string;
  type: string;
  product: string;
  name: string;
  description?: string;
  trialEnd?: number;
  trialDays?: number;
  trialAvailable: boolean;
  discount?: DiscountDto;
  scheduled?: SubscriptionScheduleDto;
  cancelAtPeriodEnd: boolean;
  image: string;
  features: string[];
  recommended: boolean;
  allowPayOnFile: boolean;
  currency: string;
  amount: number;
  upcomingPriceId?: string;
  interval: PlanInterval;
  active: boolean;
  items: SubscriptionItemDto[];
};

export type ActiveSubscriptionsDto = SubscriptionPlanDto[];

export type PlanInterval = "month" | "year";

export type UsageStatus = "NORMAL" | "WARNING" | "DEPLETED" | "TRIAL_EXCEEDED";

export type UsagePlanType = "BASE" | "FORMS" | "ADDON" | "SUPPORT" | "UNKNOWN";

export type PricingModel = "Plan" | "Usage";

export type BillingCycleDto = {
  start: number;
  end: number;
};

export type UsageTierDto = {
  upTo: number;
  usageAmount: number;
  flatAmount: number;
};

export type UsagePlanOverviewDto = {
  active: boolean;
  type: UsagePlanType;
  product: PricingModel;
  name?: string;
  currentAmount?: number;
  tiers?: UsageTierDto[];
  cycle: BillingCycleDto;
  status: UsageStatus;
};

export type BillingUsageDto = {
  plans: UsagePlanOverviewDto[];
};

export type BillingAddressDto = {
  address: string;
  zipCode: string;
  city: string;
  country: string;
  countryCode: string;
};

export type InvoiceFieldDto = {
  label: string;
  value: string;
};

export type BillingAddressPatchDto = Pick<BillingAddressDto, "address" | "zipCode" | "city" | "countryCode">;

export type BillingProfilePatchDto = Partial<
  Pick<BillingProfileDto, "companyName" | "billingEmailAddress" | "phoneNumber" | "vatNumber" | "invoiceFields">
> & { billingAddress?: BillingAddressPatchDto };

let client: AxiosInstance | null = null;
let customerId: CustomerId | null = null;

const init = (cusId: CustomerId): void => {
  customerId = cusId;
  client = useClient();
};

const getClient = () => initialized(client, "client");

const fetchInvoices = async (): Promise<Invoice[]> => {
  const { data } = await getClient().get<InvoiceDto[]>(`api/v1.0/billing/invoice/${customerId}`);
  return data.map(toInvoice);
};

const invalidateSubscriptionQueries = (queryClient: QueryClient) =>
  Promise.all(
    ["activeSubscriptions", "subscriptionStatus", "billingUsage", "upcomingInvoice"].map((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    ),
  );

const BillingService = {
  init,
  getInvoices: () =>
    useQuery<Invoice[]>(() => ({
      queryKey: ["invoices"],
      queryFn: fetchInvoices,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  hasOpenInvoices: () => {
    const queryClient = useQueryClient();
    return async (): Promise<boolean> => {
      const invoices = await queryClient.fetchQuery({ queryKey: ["invoices"], queryFn: fetchInvoices });
      return invoices.some(({ invoiceStatus }) => invoiceStatus === "OPEN");
    };
  },
  getUpcomingInvoice: () =>
    useQuery<UpcomingInvoice>(() => ({
      queryKey: ["upcomingInvoice"],
      queryFn: async () => {
        const { data } = await getClient().get<UpcomingInvoiceDto>(`api/v1.0/billing/invoice/${customerId}/upcoming`);
        return toUpcomingInvoice(data);
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  getActiveSubscriptions: () =>
    useQuery<ActiveSubscriptionsDto>(() => ({
      queryKey: ["activeSubscriptions"],
      queryFn: async () => {
        const { data } = await getClient().get<ActiveSubscriptionsDto>(`api/v1.0/billing/subscription/${customerId}`);
        return data;
      },
      throwOnError: true,
      staleTime: minutes(10),
    })),
  getSubscriptionStatus: () =>
    useQuery<SubscriptionStatusDto>(() => ({
      queryKey: ["subscriptionStatus"],
      queryFn: async () => {
        const { data } = await getClient().get<SubscriptionStatusDto>(
          `api/v1.0/billing/subscription/${customerId}/status`,
        );
        return data;
      },
      throwOnError: true,
      staleTime: minutes(10),
    })),
  getBillingUsage: () =>
    useQuery<BillingUsageDto>(() => ({
      queryKey: ["billingUsage"],
      queryFn: async () => {
        const { data } = await getClient().get<BillingUsageDto>(`api/v1.0/billing/subscription/${customerId}/usage`);
        return data;
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  getBillingProfile: () =>
    useQuery<BillingProfileDto>(() => ({
      queryKey: ["billingProfile"],
      queryFn: async () => {
        const { data } = await getClient().get<BillingProfileDto>(`api/v1.0/customers/${customerId}/billing`);
        return data;
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  patchBillingProfile: () => {
    const queryClient = useQueryClient();
    return useMutation<BillingProfileDto, MoreAppError, BillingProfilePatchDto>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().patch<BillingProfileDto, AxiosResponse<BillingProfileDto>>(
          `api/v1.0/customers/${customerId}/billing`,
          value,
        );
        return data;
      },
      // Also refetch on error: the backend saves everything except an invalid VAT number before it fails
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey: ["billingProfile"] });
      },
    }));
  },
  getCoupon: () =>
    useQuery<DiscountDto | null>(() => ({
      queryKey: ["coupon"],
      queryFn: async () => {
        try {
          const { data } = await getClient().get<DiscountDto>(`api/v1.0/billing/coupon/${customerId}`);
          return data;
        } catch (e) {
          if (e instanceof MoreAppError && e.status === 400) {
            return null;
          }
          throw e;
        }
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  addCoupon: () => {
    const queryClient = useQueryClient();
    return useMutation<DiscountDto, MoreAppError, string>(() => ({
      mutationFn: async (coupon) => {
        const { data } = await getClient().post<DiscountDto, AxiosResponse<DiscountDto>, AddCouponRequest>(
          `api/v1.0/billing/coupon/${customerId}`,
          { coupon },
        );
        return data;
      },
      onSuccess: (data) => {
        queryClient.setQueryData(["coupon"], data);
        void queryClient.invalidateQueries({ queryKey: ["upcomingInvoice"] });
      },
    }));
  },
  deleteCoupon: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, void>(() => ({
      mutationFn: async () => {
        await getClient().delete(`api/v1.0/billing/coupon/${customerId}`);
      },
      onSuccess: () => {
        queryClient.setQueryData(["coupon"], null);
        void queryClient.invalidateQueries({ queryKey: ["upcomingInvoice"] });
      },
    }));
  },
  cancelSubscription: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, CancelSubscriptionRequest>(() => ({
      mutationFn: async ({ subscription, terminationReasons, message }) => {
        await getClient().delete(`api/v1.0/billing/subscription/${customerId}/${subscription}`, {
          data: { terminationReasons, message },
        });
      },
      onSuccess: () => invalidateSubscriptionQueries(queryClient),
    }));
  },
  uncancelSubscription: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, string>(() => ({
      mutationFn: async (subscription) => {
        await getClient().post(`api/v1.0/billing/subscription/${customerId}/${subscription}/uncancel`);
      },
      onSuccess: () => invalidateSubscriptionQueries(queryClient),
    }));
  },
};

export default BillingService;
